import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, IconButton,
  TableHead, TableRow, TableContainer, Select, MenuItem, Button, Stack, Chip, TextField,
  CircularProgress, Snackbar, Alert, Tooltip, Avatar,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { ROLES, ROLE_LABEL, type Api, type TenantView, type MemberView } from './api';

const ERR: Record<string, string> = {
  last_admin: 'You can’t remove or demote the only admin.',
  already_member: 'That person is already on the team.',
  invalid_email: 'Enter a valid email address.',
};

// Deterministic, pleasant avatar colour so each teammate keeps a stable identity.
const AVATAR_COLORS = ['#0b7a75', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#0891b2', '#4f46e5', '#059669'];
function avatarColor(key: string) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(m: MemberView) {
  const src = (m.displayName ?? '').trim() || m.email;
  const parts = src.split(/[\s@._+-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function StatusPill({ status }: { status: string }) {
  const active = status === 'active';
  const color = active ? '#059669' : '#b45309';
  const bg = active ? 'rgba(5,150,105,0.10)' : 'rgba(180,83,9,0.10)';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.375,
        borderRadius: 999, bgcolor: bg, color, fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
      {status}
    </Box>
  );
}

export function Team({ api, tenant }: { api: Api; tenant: TenantView }) {
  const [members, setMembers] = useState<MemberView[] | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('EDITOR');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const load = () => { setMembers(null); api.members(tenant.tenantId).then(setMembers).catch((e) => setToast({ msg: e.message, sev: 'error' })); };
  useEffect(load, [tenant.tenantId]);
  const fail = (e: any) => setToast({ msg: ERR[e.message] ?? e.message, sev: 'error' });

  const invite = async () => {
    setBusy(true);
    try {
      const r = await api.invite(tenant.tenantId, email.trim().toLowerCase(), role);
      const to = email.trim();
      setEmail('');
      if (r.emailed) {
        setToast({ msg: `Invited — an invitation email was sent to ${to}`, sev: 'success' });
      } else if (r.reason === 'already_exists') {
        setToast({ msg: 'Added — they already have an account; the role applies on their next login', sev: 'success' });
      } else if (r.reason === 'already_invited') {
        setToast({ msg: 'Role updated — they already have a pending invitation', sev: 'success' });
      } else if (r.reason === 'idp_not_configured') {
        setToast({ msg: 'Role assigned. Invite email not sent — the identity provider isn’t configured yet (AUTH_MGMT_* in the API env).', sev: 'error' });
      } else {
        setToast({ msg: `Role assigned, but the invite email failed (${r.reason}).`, sev: 'error' });
      }
      load();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const changeRole = async (userId: string, r: string) => {
    try { await api.setRole(tenant.tenantId, userId, r); setToast({ msg: 'Role updated', sev: 'success' }); load(); }
    catch (e) { fail(e); }
  };
  const remove = async (userId: string) => {
    try { await api.removeMember(tenant.tenantId, userId); setToast({ msg: 'Removed', sev: 'success' }); load(); }
    catch (e) { fail(e); }
  };

  return (
    <Box sx={{ maxWidth: 940 }}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>Invite a teammate</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send an invitation and choose the access level they’ll have.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
            <TextField
              size="small" label="Email address" type="email" placeholder="name@company.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && email && !busy) invite(); }}
              sx={{ flexGrow: 1 }}
            />
            <Select size="small" value={role} onChange={(e) => setRole(e.target.value)} sx={{ minWidth: 170 }}>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{ROLE_LABEL[r]}</MenuItem>)}
            </Select>
            <Button
              variant="contained" startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
              onClick={invite} disabled={!email || busy} sx={{ minWidth: 108, py: 0.9 }}
            >
              Invite
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            They must also exist in your identity provider (Auth0) or be able to sign up. The role applies on their first login.
          </Typography>
        </Paper>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Members</Typography>
          {members ? (
            <Chip size="small" label={members.length} sx={{ height: 22, fontWeight: 700, bgcolor: 'rgba(11,122,117,0.10)', color: 'primary.main' }} />
          ) : null}
        </Stack>

        {!members ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
        ) : members.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <GroupOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>No teammates yet</Typography>
            <Typography variant="body2" color="text.secondary">Invite someone above to start collaborating.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'action.hover', color: 'text.secondary', fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', py: 1.25 } }}>
                  <TableCell>Member</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => {
                  const superAdmin = m.role === 'SUPER_ADMIN';
                  return (
                    <TableRow key={m.userId} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 36, height: 36, fontSize: 13.5, fontWeight: 700, bgcolor: avatarColor(m.email) }}>
                            {initials(m)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            {m.displayName ? (
                              <>
                                <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{m.displayName}</Typography>
                                <Typography noWrap sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.3 }}>{m.email}</Typography>
                              </>
                            ) : (
                              <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>{m.email}</Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {superAdmin ? (
                          <Chip size="small" label={ROLE_LABEL[m.role]} />
                        ) : (
                          <Select size="small" value={m.role} onChange={(e) => changeRole(m.userId, e.target.value)} sx={{ minWidth: 160 }}>
                            {ROLES.map((r) => <MenuItem key={r} value={r}>{ROLE_LABEL[r]}</MenuItem>)}
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusPill status={m.status} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={superAdmin ? 'Can’t remove a Super Admin here' : 'Remove'}>
                          <span>
                            <IconButton size="small" color="error" disabled={superAdmin} onClick={() => remove(m.userId)}>
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
